import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Sprout, TreeDeciduous, Crown, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { roots: Sprout, branches: TreeDeciduous, legacy: Crown };

export function PlanSelector({ onSelect, submitting, ctaLabel = "Continue", initialTier = "roots" }) {
  const [plans, setPlans] = useState(null);
  const [order, setOrder] = useState(["roots", "branches", "legacy"]);
  const [selected, setSelected] = useState(initialTier);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data.plans);
        setOrder(data.order || order);
      } catch {}
    })();
  }, []);

  if (!plans) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>;

  const priceLabel = (key) => {
    const p = plans[key].price;
    if (!p || (p.monthly === 0 && p.yearly === 0)) return "Free";
    return billing === "monthly" ? `$${p.monthly.toFixed(2)}/mo` : `$${p.yearly.toFixed(2)}/yr`;
  };

  return (
    <div data-testid="plan-selector">
      <div className="flex gap-2 p-1 rounded-2xl mb-4" style={{ background: "var(--overlay)" }}>
        {["monthly", "yearly"].map((b) => (
          <button key={b} onClick={() => setBilling(b)} data-testid={`billing-${b}`}
            className="flex-1 rounded-xl py-2 text-xs font-semibold transition-colors"
            style={billing === b ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
            {b === "monthly" ? "Monthly" : "Yearly · save more"}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {order.map((key) => {
          const p = plans[key];
          const Icon = ICONS[key] || Sprout;
          const isSel = selected === key;
          return (
            <motion.button key={key} whileTap={{ scale: 0.99 }} onClick={() => setSelected(key)}
              data-testid={`plan-option-${key}`}
              className="w-full text-left card-surface rounded-2xl p-4 transition-all"
              style={isSel ? { border: "2px solid var(--accent)", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } : { border: "1px solid var(--border)" }}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
                    <Icon size={20} className="gold-text" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl leading-tight">{p.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{p.tagline}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold gold-text" data-testid={`plan-price-${key}`}>{priceLabel(key)}</div>
                  {isSel && <Check size={16} className="gold-text ml-auto mt-1" />}
                </div>
              </div>
              {p.monthly_ai_credits != null && (
                <div className="mt-3 flex items-center gap-2 rounded-lg px-2.5 py-1.5" style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)" }} data-testid={`plan-credits-${key}`}>
                  <Sparkles size={13} className="gold-text" />
                  <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>{p.monthly_ai_credits} AI credits / month</span>
                </div>
              )}
              <ul className="space-y-1.5 mt-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <Check size={13} className="gold-text shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </motion.button>
          );
        })}
      </div>

      <motion.button whileTap={{ scale: 0.97 }} onClick={() => onSelect(selected, billing)} disabled={submitting}
        data-testid="plan-select-submit"
        className="w-full mt-5 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
        style={{ background: "var(--accent)", color: "#fff" }}>
        {submitting && <Loader2 size={16} className="animate-spin" />} {ctaLabel}
      </motion.button>
    </div>
  );
}
